import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { toPng } from 'html-to-image';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  Activity,
  Download,
  Inbox,
  Info,
  Mail,
  ShieldAlert,
} from 'lucide-react';
import { organizationsService } from '../services/organizationsService';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { api } from '../services/authService';
import { getCookie, setCookie } from '../services/cookieService';
import { getDashboardCacheDays } from '../services/cookieConfigService';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts';

/** Matches organization name for FastCube (aggregate signal counts across providers). */
const FASTCUBE_ORG = 'FastCube';

const DOMAIN_COLORS = {
  Other: '#3B82F6',
  Gmail: '#22C55E',
  Yahoo: '#F97316',
  Microsoft: '#06B6D4',
  AOL: '#A855F7',
  Apple: '#EC4899',
};

const DOMAIN_ORDER = ['Other', 'Gmail', 'Yahoo', 'Microsoft', 'AOL', 'Apple'];

const PROVIDER_COLORS = {
  LiveIntent: '#3B82F6',
  iPost: '#22C55E',
  Traverse: '#F97316',
  Other: '#A855F7',
};

const PROVIDER_ORDER = ['LiveIntent', 'iPost', 'Traverse', 'Other'];

/** Same categorical hues as the domain and provider charts; grey is reserved for the no-signal bucket. */
const ACTIVITY_SCORE_COLORS = {
  '9–10': '#3B82F6',
  '7–8': '#22C55E',
  '5–6': '#F97316',
  '3–4': '#06B6D4',
  '1–2': '#A855F7',
  'No Signal': '#A1A1AA',
};

/** Sentinel for dashboard domain filter (org-level aggregate; do not send to API as domain). */
const DOMAIN_SLICE_ALL = '__all__';

const DOMAIN_SLICE_OPTIONS = [
  { value: DOMAIN_SLICE_ALL, label: 'All' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'apple', label: 'Apple' },
  { value: 'yahoo', label: 'Yahoo' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'aol', label: 'AOL' },
  { value: 'other', label: 'Other' },
];

const ACTIVITY_RANGES = [7, 15, 30];

/** Page canvas (--bg-canvas); the export node itself has no background of its own. */
const EXPORT_BACKGROUND = '#f4f7fb';

/** The image has no header, so the organization and date live in the file name. */
function exportFileName(organization) {
  const slug = String(organization || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const parts = ['dashboard', slug, format(new Date(), 'yyyy-MM-dd')].filter(Boolean);
  return `${parts.join('-')}.png`;
}

const PANEL_TOOLTIPS = {
  uniqueEmails: 'Total number of unique email addresses, includes both opted-in and opted-out.',
  deliverableEmails: 'Total number of emails that have a valid opt-in date, no opt-out date and are not on the DNM list.',
  signalsAcrossDbsFastCube: 'Total count of all signals across all providers.',
  signalsAcrossDbsOrg: 'Total count of MD5s within your organization that have a signal',
  dnm: 'Total number of emails in the Do Not Mail list.',
  domainBreakdown: 'Domain percentages of all unique emails. Center total is known profiles when that count is available.',
  signalProviders: "Percentages represent each provider's share of the total number of signals.",
  activityScore: `Percentages represent how many emails fall into each activity bucket.

Score Breakdown:
10: 0 days (today)
9: ~1-3 days
8: ~4-10 days
7: ~11-25 days
6: ~26-50 days
5: ~51-90 days
4: ~91-151 days
3: ~152-220 days
2: ~221-300 days
1: ~301-365+ days
0: No signal date`,
  signalActivity:
    'Daily inbound signal volume for the selected window. The chart shows the most recent days from the last 30 records.',
};

const TOOLTIP_WIDTH_PX = 288;

function PanelTitleWithTooltip({ children, tooltip }) {
  const [visible, setVisible] = React.useState(false);
  const [alignRight, setAlignRight] = React.useState(false);
  const ref = React.useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const wouldOverflowRight = rect.left + TOOLTIP_WIDTH_PX > window.innerWidth;
      setAlignRight(wouldOverflowRight);
    }
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <span className="ml-1.5 cursor-help text-zinc-400 hover:text-zinc-600 flex-shrink-0" aria-label={tooltip}>
        <Info className="w-3.5 h-3.5" strokeWidth={2} />
      </span>
      <span
        role="tooltip"
        className={`absolute bottom-full mb-1 z-50 w-72 p-2.5 text-xs font-normal text-left text-white bg-zinc-900 rounded-lg shadow-lg whitespace-pre-line ${visible ? 'block' : 'hidden'} ${alignRight ? 'right-0' : 'left-0'}`}
      >
        {tooltip}
      </span>
    </span>
  );
}

function cacheKey(organizationId, suffix, domainSlice) {
  const safe = organizationId != null && organizationId !== '' ? String(organizationId) : '';
  const dom =
    domainSlice && domainSlice !== DOMAIN_SLICE_ALL
      ? `_domain_${String(domainSlice).replace(/\s+/g, '_')}`
      : '';
  return `${safe}${dom}_${suffix}`;
}

function formatCount(n) {
  if (n == null || typeof n !== 'number') return '';
  if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(3)} T`;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(3)} B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(3)} M`;
  return n.toLocaleString();
}

function formatSignalsShort(n) {
  if (n == null || typeof n !== 'number') return '';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  return n.toLocaleString();
}

/**
 * @param {{name: string, value: number}[]} fields
 * @param {Record<string, string>} colors
 * @param {{order?: string[], preserveOrder?: boolean}} [options] `preserveOrder` keeps ranked buckets in bucket order.
 */
function toChartData(fields, colors, { order = [], preserveOrder = false } = {}) {
  const named = fields
    .map((item) => ({
      ...item,
      value: Number(item.value) || 0,
      color: colors[item.name] || '#3B82F6',
    }))
    .filter(({ value }) => value > 0);
  if (preserveOrder) return named;
  named.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    const ai = order.indexOf(a.name);
    const bi = order.indexOf(b.name);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return named;
}

/** Whole-pixel sparkline geometry — fractional bar heights antialias into fuzzy tops. */
const SPARK_HEIGHT = 36;
const SPARK_BAR_WIDTH = 6;
const SPARK_BAR_GAP = 8;
const SPARK_MIN_BAR = 4;
const SPARK_MIN_NONZERO_BAR = 7;

function Sparkline({ values }) {
  if (!values?.length) return null;
  const max = Math.max(...values, 1);
  const width = values.length * SPARK_BAR_WIDTH + (values.length - 1) * SPARK_BAR_GAP;
  return (
    <svg
      width={width}
      height={SPARK_HEIGHT}
      viewBox={`0 0 ${width} ${SPARK_HEIGHT}`}
      shapeRendering="crispEdges"
      className="flex-shrink-0"
      aria-hidden="true"
    >
      {values.map((value, index) => {
        const floor = value === 0 ? SPARK_MIN_BAR : SPARK_MIN_NONZERO_BAR;
        const height = Math.max(floor, Math.round((value / max) * SPARK_HEIGHT));
        return (
          <rect
            key={index}
            x={index * (SPARK_BAR_WIDTH + SPARK_BAR_GAP)}
            y={SPARK_HEIGHT - height}
            width={SPARK_BAR_WIDTH}
            height={height}
            fill="#3B82F6"
          />
        );
      })}
    </svg>
  );
}

function normalizeCountSeries(points) {
  if (!Array.isArray(points)) return [];
  return points
    .map((point) => ({
      recorded_at: point?.recorded_at,
      value: Number(point?.value),
    }))
    .filter((point) => Number.isFinite(point.value));
}

/** Percent change from the previous snapshot to the latest (cards label this "vs last week"). */
function wowDelta(points) {
  if (!points || points.length < 2) return null;
  const latest = points[points.length - 1].value;
  const previous = points[points.length - 2].value;
  if (previous === 0) return latest === 0 ? 0 : null;
  return ((latest - previous) / previous) * 100;
}

async function fetchCountSeries(path, params) {
  try {
    const { data } = await api.get(path, { params });
    return data;
  } catch {
    return null;
  }
}

function parseDailySignalDate(value) {
  const raw = String(value || '').slice(0, 10);
  if (!raw) return null;
  const parsed = parseISO(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function mapDailySignals(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const date = parseDailySignalDate(row?.date);
      const total = Number(row?.count);
      if (!date || !Number.isFinite(total)) return null;
      return {
        date,
        label: format(date, 'MM/dd'),
        fullLabel: format(date, 'MMM d, yyyy'),
        total,
      };
    })
    .filter(Boolean);
}

function niceCeiling(n) {
  if (!(n > 0)) return 1;
  const exp = 10 ** Math.floor(Math.log10(n));
  const m = n / exp;
  const nice = m <= 1 ? 1 : m <= 1.5 ? 1.5 : m <= 2 ? 2 : m <= 3 ? 3 : m <= 4 ? 4 : m <= 5 ? 5 : m <= 6 ? 6 : m <= 8 ? 8 : 10;
  return nice * exp;
}

function activityAxis(maxValue) {
  const peak = Math.max(0, Number(maxValue) || 0);
  const domainMax = niceCeiling(peak);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((frac) => frac * domainMax);
  const divisor = peak >= 1_000_000_000 ? 1_000_000_000 : peak >= 1_000_000 ? 1_000_000 : 1;
  return {
    domainMax,
    ticks,
    tickFormatter: (value) => {
      if (divisor === 1) return Number(value).toLocaleString();
      const scaled = value / divisor;
      return Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1);
    },
  };
}

function seriesCount(payload) {
  if (payload?.count != null && Number.isFinite(Number(payload.count))) return Number(payload.count);
  const points = normalizeCountSeries(payload?.points);
  return points.length ? points[points.length - 1].value : null;
}

function WowPill({ delta }) {
  if (delta == null || Number.isNaN(Number(delta))) return null;
  const value = Number(delta);
  // Anything that rounds to 0.0% reads as flat, so it gets the neutral treatment.
  const flat = Math.abs(value) < 0.05;
  const up = value > 0;
  const tone = flat
    ? 'bg-[#E8EEF5] text-zinc-500'
    : up
      ? 'bg-[#DCFCE7] text-[#15803D]'
      : 'bg-[#FEE2E2] text-[#DC2626]';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone}`}>
      {flat ? '→' : up ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function ActivityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 shadow-[0_8px_24px_rgba(24,24,27,0.08)]">
      <p className="text-[11px] text-zinc-400">{row.fullLabel}</p>
      <p className="mt-1 text-sm text-[#18181B]">
        Signals <span className="font-semibold tabular-nums">{formatSignalsShort(row.total)}</span>
      </p>
    </div>
  );
}

/** Tick-donut geometry — see dashboard-design-spec.md DOMAIN BREAKDOWN. */
const DONUT_SIZE = 280;
const DONUT_TICKS = 96;
const DONUT_INNER_R = 112;
const DONUT_OUTER_R = 132;
const DONUT_STROKE = 5.5;

/** Annular wedge path used as an invisible hover target over a slice's ticks. */
function wedgePath(cx, cy, innerR, outerR, startAngle, endAngle) {
  const point = (r, a) => `${cx + Math.cos(a) * r} ${cy + Math.sin(a) * r}`;
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${point(outerR, startAngle)}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${point(outerR, endAngle)}`,
    `L ${point(innerR, endAngle)}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${point(innerR, startAngle)}`,
    'Z',
  ].join(' ');
}

/**
 * Tick-donut with per-slice hover: the tooltip names the slice so the chart is
 * readable without relying on color alone.
 */
function TickDonut({ data, centerValue, centerLabel, label }) {
  const [hovered, setHovered] = useState(null);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const cx = DONUT_SIZE / 2;
  let cumulative = 0;
  const boundaries = data.map((item) => {
    cumulative += total ? item.value / total : 0;
    return cumulative;
  });
  const sliceOf = (ratio) => {
    const index = boundaries.findIndex((boundary) => ratio <= boundary);
    return index === -1 ? data.length - 1 : index;
  };
  const hoveredEntry = hovered != null ? data[hovered] : null;

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[280px]">
      <svg
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        className="h-full w-full"
        role="img"
        aria-label={label}
        onMouseLeave={() => setHovered(null)}
      >
        {Array.from({ length: DONUT_TICKS }, (_, index) => {
          const ratio = (index + 0.5) / DONUT_TICKS;
          const colorIndex = sliceOf(ratio);
          const angle = (index / DONUT_TICKS) * Math.PI * 2 - Math.PI / 2;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const dimmed = hovered != null && hovered !== colorIndex;
          return (
            <line
              key={index}
              x1={cx + cos * DONUT_INNER_R}
              y1={cx + sin * DONUT_INNER_R}
              x2={cx + cos * DONUT_OUTER_R}
              y2={cx + sin * DONUT_OUTER_R}
              stroke={data[colorIndex]?.color || '#E2E8F0'}
              strokeWidth={DONUT_STROKE}
              strokeLinecap="round"
              opacity={dimmed ? 0.25 : 1}
            />
          );
        })}
        {data.map((entry, index) => {
          const start = (index === 0 ? 0 : boundaries[index - 1]) * Math.PI * 2 - Math.PI / 2;
          // A slice covering the whole ring would close on itself and lose its hover area.
          const end = Math.min(boundaries[index] * Math.PI * 2 - Math.PI / 2, start + Math.PI * 2 - 0.001);
          if (!(end > start)) return null;
          return (
            <path
              key={entry.name}
              d={wedgePath(cx, cx, DONUT_INNER_R - 6, DONUT_OUTER_R + 6, start, end)}
              fill="transparent"
              onMouseEnter={() => setHovered(index)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(null)}
              tabIndex={0}
              className="cursor-pointer focus:outline-none"
            >
              <title>{`${entry.name}: ${formatCount(entry.value)}${total ? ` (${((entry.value / total) * 100).toFixed(1)}%)` : ''}`}</title>
            </path>
          );
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
        {hoveredEntry ? (
          <>
            <span className="flex items-center gap-1.5 text-xs font-medium text-zinc-500">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: hoveredEntry.color }} />
              {hoveredEntry.name}
            </span>
            <span className="mt-1 text-[26px] font-bold leading-none tracking-tight text-[#18181B] tabular-nums">
              {total ? `${((hoveredEntry.value / total) * 100).toFixed(1)}%` : '0.0%'}
            </span>
            <span className="mt-1 text-xs text-zinc-400 tabular-nums">{formatCount(hoveredEntry.value)}</span>
          </>
        ) : (
          <>
            <span className="text-[28px] font-bold tracking-tight text-[#18181B] tabular-nums">
              {centerValue != null ? formatCount(centerValue) : formatCount(total)}
            </span>
            <span className="mt-1 text-xs text-zinc-400">{centerLabel}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Donut panel with legend rows — shared by domain, provider and activity-score breakdowns. */
function DonutPanel({ title, tooltip, data, loading, centerValue, centerLabel }) {
  const total = data?.reduce((sum, item) => sum + item.value, 0) || 0;
  return (
    <div className="rounded-card border border-hairline bg-surface p-6 shadow-card">
      <PanelTitleWithTooltip tooltip={tooltip}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">{title}</h3>
      </PanelTitleWithTooltip>
      {loading ? (
        <div className="mx-auto mt-6 aspect-square w-full max-w-[280px] animate-pulse rounded-full bg-zinc-50" />
      ) : !data?.length ? (
        <div className="mx-auto flex aspect-square w-full max-w-[280px] items-center justify-center text-sm text-zinc-400">No data available</div>
      ) : (
        <>
          <div className="mt-4">
            <TickDonut data={data} centerValue={centerValue} centerLabel={centerLabel} label={title} />
          </div>
          <div className="mt-4 space-y-2.5">
            {data.map((entry) => (
              <div key={entry.name} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-xs">
                <span className="flex min-w-0 items-center gap-2 text-zinc-600">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="truncate">{entry.name}</span>
                </span>
                <span className="text-zinc-400 tabular-nums">{formatCount(entry.value)}</span>
                <span className="w-12 text-right font-semibold text-[#18181B] tabular-nums">
                  {total ? `${((entry.value / total) * 100).toFixed(1)}%` : '0.0%'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const KPI_SERIES_LIMIT = 5;
const ACTIVITY_SERIES_LIMIT = 30;

const EMPTY_KPI_SERIES = {
  totalSignals: [],
  uniqueEmails: [],
  deliverableEmails: [],
  dnmCount: [],
};

function Dashboard() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [selectedDomainSlice, setSelectedDomainSlice] = useState(DOMAIN_SLICE_ALL);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [domainSummaryData, setDomainSummaryData] = useState(null);
  const [signalProvidersData, setSignalProvidersData] = useState(null);
  const [activityScoreData, setActivityScoreData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState(null);
  const [activeProviderCount, setActiveProviderCount] = useState(null);
  const [uniqueEmailCount, setUniqueEmailCount] = useState(null);
  const [knownProfileMd5Count, setKnownProfileMd5Count] = useState(null);
  const [dnmEmailCount, setDnmEmailCount] = useState(null);
  const [deliverableEmailsCount, setDeliverableEmailsCount] = useState(null);
  const [totalSignalCount, setTotalSignalCount] = useState(null);
  const [kpiSeries, setKpiSeries] = useState(EMPTY_KPI_SERIES);
  const [countsLoading, setCountsLoading] = useState(true);
  const [activityRange, setActivityRange] = useState(15);
  const [activitySeries, setActivitySeries] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);
  const [activeBarIndex, setActiveBarIndex] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  /** The greeting and filter row are dropped from the exported image. */
  const headerRef = useRef(null);

  const getDisplayName = () => {
    if (user?.first_name) return user.first_name;
    if (user?.email) {
      const localPart = user.email.split('@')[0];
      const parts = localPart.split(/[._]/);
      return parts
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
        .split(' ')[0];
    }
    return null;
  };

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some((o) => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch {
        // ignore; keep defaults
      } finally {
        setOrganizationsLoading(false);
      }
    };
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (organizationsLoading || !organizations?.length) return;
    const isInList = organizations.some((o) => o.name === selectedOrganization);
    if (!selectedOrganization || isInList) return;
    const next = organizations[0]?.name ?? '';
    setSelectedOrganization(next);
    organizationsService.setSelectedOrganization(next, 'name', organizations);
    if (!next) {
      setDomainSummaryData(null);
      setSignalProvidersData(null);
      setActivityScoreData(null);
      setChartsError(null);
      setActiveProviderCount(null);
      setUniqueEmailCount(null);
      setKnownProfileMd5Count(null);
      setDnmEmailCount(null);
      setDeliverableEmailsCount(null);
      setTotalSignalCount(null);
      setKpiSeries(EMPTY_KPI_SERIES);
    }
  }, [organizationsLoading, organizations, selectedOrganization]);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!selectedOrganization) {
        setChartsLoading(false);
        setCountsLoading(false);
        setDomainSummaryData(null);
        setSignalProvidersData(null);
        setActivityScoreData(null);
        setChartsError(null);
        setActiveProviderCount(null);
        setUniqueEmailCount(null);
        setKnownProfileMd5Count(null);
        setDnmEmailCount(null);
        setDeliverableEmailsCount(null);
        setTotalSignalCount(null);
        setKpiSeries(EMPTY_KPI_SERIES);
        return;
      }
      setDomainSummaryData(null);
      setSignalProvidersData(null);
      setActivityScoreData(null);
      setChartsError(null);
      setActiveProviderCount(null);
      setUniqueEmailCount(null);
      setKnownProfileMd5Count(null);
      setDnmEmailCount(null);
      setDeliverableEmailsCount(null);
      setTotalSignalCount(null);
      setKpiSeries(EMPTY_KPI_SERIES);
      setChartsLoading(true);
      setCountsLoading(true);
      const orgRecord = organizations.find((o) => o.name === selectedOrganization);
      const organizationId = orgRecord?.id;
      if (organizationId == null || organizationId === '') {
        setChartsLoading(false);
        setCountsLoading(false);
        return;
      }
      const keyDashboard = cacheKey(organizationId, 'dashboard_v3', selectedDomainSlice);

      const applyDomain = (d) => {
        if (!d) return;
        setDomainSummaryData(toChartData([
          { name: 'Other', value: d.other_domain_md5_ct ?? 0 },
          { name: 'Gmail', value: d.gmail_md5_ct ?? 0 },
          { name: 'Yahoo', value: d.yahoo_md5_ct ?? 0 },
          { name: 'Microsoft', value: d.microsoft_md5_ct ?? 0 },
          { name: 'AOL', value: d.aol_md5_ct ?? 0 },
          { name: 'Apple', value: d.apple_md5_ct ?? 0 },
        ], DOMAIN_COLORS, { order: DOMAIN_ORDER }));
      };
      const applyProviders = (d) => {
        if (!d) return;
        setSignalProvidersData(toChartData([
          { name: 'LiveIntent', value: d.live_intent_md5_ct ?? 0 },
          { name: 'iPost', value: d.ipost_md5_ct ?? 0 },
          { name: 'Traverse', value: d.traverse_md5_ct ?? 0 },
          { name: 'Other', value: d.other_provider_md5_ct ?? 0 },
        ], PROVIDER_COLORS, { order: PROVIDER_ORDER }));
      };
      const applyActivityScore = (d) => {
        if (!d) return;
        setActivityScoreData(toChartData([
          { name: '9–10', value: d.activity_score_9_10 ?? 0 },
          { name: '7–8', value: d.activity_score_7_8 ?? 0 },
          { name: '5–6', value: d.activity_score_5_6 ?? 0 },
          { name: '3–4', value: d.activity_score_3_4 ?? 0 },
          { name: '1–2', value: d.activity_score_1_2 ?? 0 },
          { name: 'No Signal', value: d.md5_emails_with_no_signal_ct ?? 0 },
        ], ACTIVITY_SCORE_COLORS, { preserveOrder: true }));
      };
      const applyData = (data) => {
        applyDomain(data.domain_summary);
        applyProviders(data.signal_providers);
        applyActivityScore(data.activity_score);
        setActiveProviderCount(data.active_provider_count ?? null);
        setKnownProfileMd5Count(data.known_profile_md5_count ?? null);
      };
      const applyKpis = (kpis) => {
        setTotalSignalCount(seriesCount(kpis.totalSignals));
        setUniqueEmailCount(seriesCount(kpis.uniqueEmails));
        setDeliverableEmailsCount(seriesCount(kpis.deliverableEmails));
        setDnmEmailCount(seriesCount(kpis.dnmCount));
        setKpiSeries({
          totalSignals: normalizeCountSeries(kpis.totalSignals?.points),
          uniqueEmails: normalizeCountSeries(kpis.uniqueEmails?.points),
          deliverableEmails: normalizeCountSeries(kpis.deliverableEmails?.points),
          dnmCount: normalizeCountSeries(kpis.dnmCount?.points),
        });
      };

      try {
        const cached = getCookie(keyDashboard);
        if (cached) {
          try {
            const bundle = JSON.parse(cached);
            if (bundle?.dashboard && bundle?.kpis) {
              applyData(bundle.dashboard);
              applyKpis(bundle.kpis);
              setChartsLoading(false);
              setCountsLoading(false);
              return;
            }
          } catch { /* ignore */ }
        }

        const params = { organization_id: organizationId };
        if (selectedDomainSlice !== DOMAIN_SLICE_ALL) {
          params.domain = selectedDomainSlice;
        }
        const countParams = { ...params, limit: KPI_SERIES_LIMIT };
        const [dashRes, totalSignals, uniqueEmails, deliverableEmails, dnmCount] = await Promise.all([
          api.get('/api/v2/signal_analysis/dashboard', { params }),
          fetchCountSeries('/api/v2/signal_analysis/total_signal_count', countParams),
          fetchCountSeries('/api/v2/signal_analysis/unique_email_count', countParams),
          fetchCountSeries('/api/v2/signal_analysis/deliverable_emails_count', countParams),
          fetchCountSeries('/api/v2/signal_analysis/dnm_email_count', countParams),
        ]);
        const data = dashRes.data;
        const kpis = { totalSignals, uniqueEmails, deliverableEmails, dnmCount };
        applyData(data);
        applyKpis(kpis);
        const hasData =
          data.domain_summary != null ||
          data.signal_providers != null ||
          data.activity_score != null ||
          data.known_profile_md5_count != null ||
          data.active_provider_count != null ||
          kpis.totalSignals != null ||
          kpis.uniqueEmails != null ||
          kpis.deliverableEmails != null ||
          kpis.dnmCount != null;
        if (hasData) {
          setCookie(keyDashboard, JSON.stringify({ dashboard: data, kpis }), getDashboardCacheDays());
        }
      } catch {
        setChartsError('Unable to load chart data');
      } finally {
        setChartsLoading(false);
        setCountsLoading(false);
      }
    };
    loadDashboard();
  }, [selectedOrganization, selectedDomainSlice, organizations]);

  useEffect(() => {
    let cancelled = false;
    const loadActivity = async () => {
      setActivityLoading(true);
      setActivityError(null);
      try {
        const { data } = await api.get('/api/v2/daily_signals', { params: { limit: ACTIVITY_SERIES_LIMIT } });
        if (!cancelled) setActivitySeries(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) {
          setActivitySeries([]);
          setActivityError('Unable to load signal activity');
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };
    loadActivity();
    return () => {
      cancelled = true;
    };
  }, []);

  const activityData = useMemo(() => {
    const mapped = mapDailySignals(activitySeries);
    if (mapped.length <= activityRange) return mapped;
    return mapped.slice(mapped.length - activityRange);
  }, [activitySeries, activityRange]);

  const activityYAxis = useMemo(() => {
    const peak = activityData.reduce((max, row) => Math.max(max, row.total), 0);
    return activityAxis(peak);
  }, [activityData]);

  useEffect(() => {
    if (!activityData.length) {
      setActiveBarIndex(null);
      return;
    }
    let peak = 0;
    activityData.forEach((row, index) => {
      if (row.total >= activityData[peak].total) peak = index;
    });
    setActiveBarIndex(peak);
  }, [activityData]);

  const handleExportPng = async () => {
    const node = exportRef.current;
    if (!node || exporting) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: EXPORT_BACKGROUND,
        cacheBust: true,
        filter: (element) => element !== headerRef.current,
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = exportFileName(selectedOrganization);
      link.click();
    } catch {
      toast.error('Unable to export the dashboard image');
    } finally {
      setExporting(false);
    }
  };

  const displayName = getDisplayName();
  const providerLabel =
    activeProviderCount == null
      ? 'across connected providers'
      : `across ${activeProviderCount} provider${activeProviderCount === 1 ? '' : 's'}`;

  const metrics = [
    {
      key: 'totalSignals',
      label: !selectedOrganization || selectedOrganization === FASTCUBE_ORG ? 'Total Signals' : 'MD5s with Signals',
      value: totalSignalCount,
      tooltip: !selectedOrganization || selectedOrganization === FASTCUBE_ORG ? PANEL_TOOLTIPS.signalsAcrossDbsFastCube : PANEL_TOOLTIPS.signalsAcrossDbsOrg,
      icon: Activity,
      tint: 'bg-blue-50 text-[#3B82F6]',
      detail: providerLabel,
      delta: wowDelta(kpiSeries.totalSignals),
      series: kpiSeries.totalSignals,
    },
    {
      key: 'uniqueEmails',
      label: 'Unique Emails',
      value: uniqueEmailCount,
      tooltip: PANEL_TOOLTIPS.uniqueEmails,
      icon: Mail,
      tint: 'bg-emerald-50 text-[#22C55E]',
      detail: 'known profiles matched',
      delta: wowDelta(kpiSeries.uniqueEmails),
      series: kpiSeries.uniqueEmails,
    },
    {
      key: 'deliverableEmails',
      label: 'Deliverable Emails',
      value: deliverableEmailsCount,
      tooltip: PANEL_TOOLTIPS.deliverableEmails,
      icon: Inbox,
      tint: 'bg-sky-50 text-[#0EA5E9]',
      detail: 'verified reachable',
      delta: wowDelta(kpiSeries.deliverableEmails),
      series: kpiSeries.deliverableEmails,
    },
    {
      key: 'dnmCount',
      label: 'DNM Count',
      value: dnmEmailCount,
      tooltip: PANEL_TOOLTIPS.dnm,
      icon: ShieldAlert,
      tint: 'bg-rose-50 text-[#EF4444]',
      detail: 'do-not-mail suppressed',
      delta: wowDelta(kpiSeries.dnmCount),
      series: kpiSeries.dnmCount,
    },
  ];

  const knownProfilesCenter = knownProfileMd5Count ?? uniqueEmailCount;

  return (
    <div ref={exportRef} className="space-y-7 pb-8">
      <section ref={headerRef} className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-[-0.035em] text-[#18181B] sm:text-[40px]">
            Welcome back{displayName ? `, ${displayName}` : ''}
          </h1>
          <p className="mt-2 text-sm text-zinc-500">here&apos;s your data pipeline today.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-72">
            {organizationsLoading || organizations.length === 0 ? (
              <FunctionalSelect
                value=""
                onValueChange={() => {}}
                placeholder={organizationsLoading ? 'Loading...' : 'No organizations'}
                className="w-full"
                disabled
                label="Organization"
              />
            ) : (
              <FunctionalSelect
                id="dashboard-organization"
                value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
                onValueChange={(value) => {
                  const parsed = organizationsService.parseOrganizationOptionValue(value);
                  const name = parsed?.name || value;
                  setSelectedOrganization(name);
                  organizationsService.setSelectedOrganization(name, 'name', parsed ? [{ name, id: parsed.id }] : organizations);
                }}
                placeholder="Select organization"
                className="w-full"
                label="Organization"
              >
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>{org.name}</SelectItem>
                ))}
              </FunctionalSelect>
            )}
          </div>
          <div className="w-[calc(50%-4px)] sm:w-36">
            <FunctionalSelect
              id="dashboard-domain"
              value={selectedDomainSlice}
              onValueChange={setSelectedDomainSlice}
              placeholder="All"
              className="w-full"
              disabled={organizationsLoading || organizations.length === 0}
              label="Domain"
            >
              {DOMAIN_SLICE_OPTIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </FunctionalSelect>
          </div>
          <button
            type="button"
            onClick={handleExportPng}
            disabled={exporting}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#0062FB] text-white hover:bg-[#0052E0] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={exporting ? 'Exporting PNG' : 'Download dashboard as PNG'}
            title="Download PNG"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <div key={metric.key} className="rounded-card border border-hairline bg-surface p-5 shadow-card min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${metric.tint}`}>
                  <metric.icon className="h-4 w-4" />
                </span>
                <PanelTitleWithTooltip tooltip={metric.tooltip}>
                  <span className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">{metric.label}</span>
                </PanelTitleWithTooltip>
              </div>
            </div>
            <div className="mt-5 flex items-end justify-between gap-3">
              <div className="min-h-[36px] min-w-0">
                {countsLoading ? (
                  <div className="h-8 w-24 animate-pulse rounded-md bg-zinc-100" />
                ) : metric.value !== null ? (
                  <p className="text-[30px] font-bold leading-none tracking-tight text-[#18181B] tabular-nums">{formatCount(metric.value)}</p>
                ) : (
                  <p className="text-sm text-zinc-400">No data available</p>
                )}
              </div>
              <Sparkline values={metric.series.map((point) => point.value)} />
            </div>
            <p className="mt-3 truncate text-xs text-zinc-400">{metric.detail}</p>
            <div className="mt-5 flex items-center justify-between border-t border-[#EEF2F7] pt-3 text-xs text-zinc-400">
              <span>vs last week</span>
              <WowPill delta={metric.delta} />
            </div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-card border border-hairline bg-surface p-6 shadow-card xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PanelTitleWithTooltip tooltip={PANEL_TOOLTIPS.signalActivity}>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">Signal activity</h3>
            </PanelTitleWithTooltip>
            <div className="inline-flex rounded-lg bg-[#E8EEF5] p-0.5" role="group" aria-label="Activity range">
              {ACTIVITY_RANGES.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setActivityRange(days)}
                  className={`h-7 min-w-[40px] rounded-md px-2.5 text-[11px] font-semibold ${
                    activityRange === days ? 'bg-[#18181B] text-white' : 'text-zinc-500 hover:text-[#18181B]'
                  }`}
                >
                  {days}D
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 h-[310px]">
            {activityLoading ? (
              <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
            ) : !activityData.length ? (
              <div className="flex h-full items-center justify-center text-sm text-zinc-400">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={activityData}
                margin={{ top: 16, right: 4, left: 0, bottom: 8 }}
                onMouseMove={(state) => {
                  if (state?.activeTooltipIndex != null) setActiveBarIndex(state.activeTooltipIndex);
                }}
              >
                <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="4 4" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A1A1AA', fontSize: 11 }}
                  dy={8}
                  minTickGap={12}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A1A1AA', fontSize: 11 }}
                  ticks={activityYAxis.ticks}
                  domain={[0, activityYAxis.domainMax]}
                  tickFormatter={activityYAxis.tickFormatter}
                  width={36}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(243,242,238,0.7)' }}
                  content={<ActivityTooltip />}
                  defaultIndex={activeBarIndex ?? undefined}
                />
                <Bar dataKey="total" maxBarSize={28} radius={[6, 6, 0, 0]}>
                  {activityData.map((entry, index) => (
                    <Cell
                      key={format(entry.date, 'yyyy-MM-dd')}
                      fill={index === activeBarIndex ? '#2563EB' : '#3B82F6'}
                    />
                  ))}
                </Bar>
              </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <DonutPanel
          title="Domain breakdown"
          tooltip={PANEL_TOOLTIPS.domainBreakdown}
          data={domainSummaryData}
          loading={chartsLoading}
          centerValue={knownProfilesCenter}
          centerLabel="known profiles"
        />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DonutPanel
          title="Signal providers"
          tooltip={PANEL_TOOLTIPS.signalProviders}
          data={signalProvidersData}
          loading={chartsLoading}
          centerLabel="signals"
        />
        <DonutPanel
          title="Activity score"
          tooltip={PANEL_TOOLTIPS.activityScore}
          data={activityScoreData}
          loading={chartsLoading}
          centerLabel="scored emails"
        />
      </section>
      {chartsError && <p className="text-sm text-amber-600">{chartsError}</p>}
      {activityError && <p className="text-sm text-amber-600">{activityError}</p>}
    </div>
  );
}

export default Dashboard;
